import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { X, Lock, Wrench, Info, AlertTriangle, Star, Zap, Bell, Megaphone, Clock, CheckCircle, Flame, Gift, ShieldAlert, Rocket, PartyPopper, RefreshCw, Heart, ThumbsUp, Coffee, WifiOff, CloudOff, Server, CreditCard, Truck, Smile } from 'lucide-react';

const BYPASS_CODE = '051111';

export const BANNER_ICONS = {
  geen:         { label: 'Geen',         icon: null,                                          preview: null },
  slot:         { label: 'Slot',         icon: <Lock size={40} color="#fff" />,               preview: <Lock size={16} /> },
  onderhoud:    { label: 'Onderhoud',    icon: <Wrench size={40} color="#fff" />,             preview: <Wrench size={16} /> },
  info:         { label: 'Info',         icon: <Info size={40} color="#fff" />,               preview: <Info size={16} /> },
  waarschuwing: { label: 'Waarschuwing', icon: <AlertTriangle size={40} color="#fff" />,      preview: <AlertTriangle size={16} /> },
  ster:         { label: 'Ster',         icon: <Star size={40} color="#fff" />,               preview: <Star size={16} /> },
  zap:          { label: 'Zap',          icon: <Zap size={40} color="#fff" />,               preview: <Zap size={16} /> },
  bell:         { label: 'Bell',         icon: <Bell size={40} color="#fff" />,               preview: <Bell size={16} /> },
  megaphone:    { label: 'Megafoon',     icon: <Megaphone size={40} color="#fff" />,          preview: <Megaphone size={16} /> },
  klok:         { label: 'Klok',         icon: <Clock size={40} color="#fff" />,              preview: <Clock size={16} /> },
  check:        { label: 'Check',        icon: <CheckCircle size={40} color="#fff" />,        preview: <CheckCircle size={16} /> },
  vuur:         { label: 'Vuur',         icon: <Flame size={40} color="#fff" />,              preview: <Flame size={16} /> },
  cadeau:       { label: 'Cadeau',       icon: <Gift size={40} color="#fff" />,               preview: <Gift size={16} /> },
  shield:       { label: 'Shield',       icon: <ShieldAlert size={40} color="#fff" />,        preview: <ShieldAlert size={16} /> },
  rocket:       { label: 'Raket',        icon: <Rocket size={40} color="#fff" />,             preview: <Rocket size={16} /> },
  feest:        { label: 'Feest',        icon: <PartyPopper size={40} color="#fff" />,        preview: <PartyPopper size={16} /> },
  update:       { label: 'Update',       icon: <RefreshCw size={40} color="#fff" />,          preview: <RefreshCw size={16} /> },
  heart:        { label: 'Hart',         icon: <Heart size={40} color="#fff" />,              preview: <Heart size={16} /> },
  thumbsup:     { label: 'Duim omhoog',  icon: <ThumbsUp size={40} color="#fff" />,          preview: <ThumbsUp size={16} /> },
  coffee:       { label: 'Koffie',       icon: <Coffee size={40} color="#fff" />,             preview: <Coffee size={16} /> },
  wifioff:      { label: 'Geen wifi',    icon: <WifiOff size={40} color="#fff" />,            preview: <WifiOff size={16} /> },
  cloudoff:     { label: 'Cloud offline',icon: <CloudOff size={40} color="#fff" />,           preview: <CloudOff size={16} /> },
  server:       { label: 'Server',       icon: <Server size={40} color="#fff" />,             preview: <Server size={16} /> },
  betaling:     { label: 'Betaling',     icon: <CreditCard size={40} color="#fff" />,         preview: <CreditCard size={16} /> },
  bezorging:    { label: 'Bezorging',    icon: <Truck size={40} color="#fff" />,              preview: <Truck size={16} /> },
  smile:        { label: 'Smile',        icon: <Smile size={40} color="#fff" />,              preview: <Smile size={16} /> },
};

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(-100%); }
  to   { opacity: 1; transform: translateY(0); }
`;

const BannerBar = styled.div`
  background: ${props => props.$color === 'mode'
    ? (props.theme?.mode === 'light' ? '#ffffff' : '#000000')
    : (props.$color || '#ea7211')};
  color: ${props => props.$color === 'mode'
    ? (props.theme?.mode === 'light' ? '#000000' : '#ffffff')
    : '#fff'};
  text-align: center;
  padding: 10px 56px 11px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.1px;
  box-shadow: 0 4px 20px rgba(255,255,255,0.12);
  flex-shrink: 0;
  position: relative;
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  animation: ${slideIn} 0.7s cubic-bezier(0.16, 1, 0.3, 1);
`;

const BannerButton = styled.a`
  background: rgba(255,255,255,0.2);
  border: 1px solid rgba(255,255,255,0.35);
  border-radius: 100px;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  padding: 4px 12px;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.15s;
  &:hover { background: rgba(255,255,255,0.32); }
`;

const DismissButton = styled.button`
  position: absolute;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  @media (max-width: 768px) { right: 16px; }
  background: ${props => props.$mode ? 'rgba(128,128,128,0.15)' : 'rgba(255,255,255,0.2)'};
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: ${props => props.$mode ? 'rgba(128,128,128,0.25)' : 'rgba(255,255,255,0.35)'}; }
`;

const BlockOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: #000;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${slideIn} 0.7s cubic-bezier(0.16, 1, 0.3, 1);
`;

const BlockCard = styled.div`
  background: #111;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 20px;
  padding: 40px;
  max-width: 680px;
  width: 90%;
  text-align: center;
  box-shadow: 0 24px 60px rgba(0,0,0,0.8);
`;

const BlockIcon = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
`;

const BlockTitle = styled.h2`
  color: #fff;
  font-size: 26px;
  font-weight: 700;
  margin: 0 0 12px;
  letter-spacing: -0.5px;
`;

const BlockMessage = styled.p`
  color: rgba(255,255,255,0.45);
  font-size: 20px;
  line-height: 1.5;
  margin: 0 0 28px;
`;

const CodeInput = styled.input`
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 12px;
  color: #fff;
  font-size: 18px;
  font-family: monospace;
  letter-spacing: 6px;
  text-align: center;
  padding: 12px 20px;
  width: 180px;
  outline: none;
  transition: border-color 0.15s;
  &:focus { border-color: rgba(255,255,255,0.3); }
  &::placeholder { letter-spacing: 2px; color: rgba(255,255,255,0.2); }
  ${props => props.$error && `border-color: #ff3b30;`}
`;

const AppBanner = ({ banner, visible, dismiss }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);
  const [bypassed, setBypassed] = useState(false);
  const [ready, setReady] = useState(false);

  React.useEffect(() => {
    const t = setTimeout(() => setReady(true), 1500);
    return () => clearTimeout(t);
  }, []);

  // Role filtering
  const stored = (() => { try { return JSON.parse(localStorage.getItem('kmfc_user') || '{}'); } catch { return {}; } })();
  const userRoleId = stored.role_id;
  const roleAllowed = !banner?.roles?.length || banner.roles.includes(userRoleId);
  const expired = banner?.expires_at && new Date(banner.expires_at) < new Date();

  if (!banner || !visible || bypassed || !ready || !roleAllowed || expired) return null;

  if (banner.block_screen) {
    const handleCodeChange = (e) => {
      const val = e.target.value.replace(/\D/g, '').slice(0, 6);
      setCode(val);
      setError(false);
      if (val.length === 6) {
        if (val === BYPASS_CODE) {
          setBypassed(true);
        } else {
          setError(true);
          setTimeout(() => { setCode(''); setError(false); }, 600);
        }
      }
    };

    return (
      <BlockOverlay>
        <BlockCard>
          <BlockIcon>{BANNER_ICONS[banner.icon]?.icon}</BlockIcon>
          <BlockTitle>{banner.title || 'Tijdelijk niet beschikbaar'}</BlockTitle>
          <BlockMessage>{banner.message}</BlockMessage>
          <CodeInput
            type="password"
            inputMode="numeric"
            placeholder="······"
            value={code}
            onChange={handleCodeChange}
            $error={error}
            autoFocus
          />
        </BlockCard>
      </BlockOverlay>
    );
  }

  const BannerIcon = BANNER_ICONS[banner.icon];

  return (
    <BannerBar $color={banner.color}>
      {BannerIcon?.preview && React.cloneElement(BannerIcon.preview, { size: 14, style: { flexShrink: 0 } })}
      <span>{banner.message}</span>
      {banner.button_label && banner.button_url && (
        <BannerButton href={banner.button_url} target="_blank" rel="noopener noreferrer">
          {banner.button_label}
        </BannerButton>
      )}
      {banner.dismissable && (
        <DismissButton onClick={dismiss}>
          <X size={13} />
        </DismissButton>
      )}
    </BannerBar>
  );
};

export default AppBanner;
